import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { Constants } from '@constants';
import { Tabs } from '@enums/tab.enum';
import { EstimateBaseComponent } from '@fs/components/base/estimate-base.component';
import { ActionItemsModel } from '@fs/models/action-menu.model';
import { CodeTypeModel } from '@fs/models/code-type.model';
import { EstimateOptionDiscountResponseModel, EstimateResponseModel } from '@fs/models/estimate/estimate-response.model';
import { WorkTicketPaymentSummary } from '@fs/models/work-order/work-order.model';
import { DispatcherService } from '@fs/services/dispatcher/dispatcher.service';
import { EstimateService } from '@fs/services/estimate/estimate.service';
import { TabParams } from '@fs/services/tab-params.service';
import { WorkOrderService } from '@fs/services/work-order/work-order.service';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { CommonMethods } from '@services/common.method';
import { PrintService } from '@services/print-layout.service';
import { TabOpenService } from '@services/tab-open.service';
import { ActionName } from '@shared/enums/action-name';
import { PermissionPipe } from '@shared/pipes/permission.pipe';
import { NotificationsService } from '@shared/services/notification-service';
import { SpinnerService } from '@shared/services/spinner.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface NoteModel {
  internal: string;
  external: string;
}
@Component({
  selector: 'estimate-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
  providers: [EstimateService, DatePipe]
})
export class EstimateViewComponent extends EstimateBaseComponent implements OnInit {
  tabCounter: number;
  inputParams: any;
  data: EstimateResponseModel;
  paymentDetails: WorkTicketPaymentSummary = new WorkTicketPaymentSummary();
  agreementDiscount: number = 0;
  note: NoteModel;
  slideIndex: number = 0;
  showAttachmentSlider: boolean = false;
  priceBookItems: GridDataResult = { data: [], total: 0 };
  attachments: GridDataResult = { data: [], total: 0 };
  discountDetails: EstimateOptionDiscountResponseModel;
  actionName = ActionName;
  locked = false;
  confirmationBox = { type: '', message: '', code: '', show: false };
  items: ActionItemsModel[] = [
    { text: 'Undo Dispatching', disabled: !this.permissionPipe.transform(this.actionName.EditEstimate) }
  ];
  showConvertEstimate = false;
  isApproveAndConvert = false;
  protected readonly ngUnsubscribe$ = new Subject();

  constructor(private estimateService: EstimateService, private tabOpenService: TabOpenService, private datePipe: DatePipe,
    private spinnerService: SpinnerService, private viewRef: ViewContainerRef, private commonMethods: CommonMethods,
    private printService: PrintService, private notificationService: NotificationsService, private permissionPipe: PermissionPipe, 
    private dispatcherService: DispatcherService, private workOrderService: WorkOrderService) { 
    super();
  }
  ngOnInit() {
    this.initSubTabs();
    this.getData();
  }
  getData() {
    this.spinnerService.showloader(this.viewRef, this.tabCounter, 0);
    this.estimateService.GetEstimate(this.inputParams.CustomerID, this.inputParams.JobSiteID, this.inputParams.ID)
      .pipe(takeUntil(this.ngUnsubscribe$)).subscribe(x => {
        this.spinnerService.hideLoader(this.viewRef, this.tabCounter);
        this.data = x;
        this.setAppointment();
        this.setNotes();
        this.setEstimateOptions();
        if (!this.locked && this.data.Status) {
          this.locked = this.data.Status.Code.toLowerCase() === 'converted';
        }
        if (this.data.Duration) {
          let durationArr = this.data.Duration.split(':');
          if (durationArr[0].charAt(0) == '0') { 
            durationArr[0] = durationArr[0].charAt(1) 
          };
          this.data.Duration = `${durationArr[0]}h : ${durationArr[1]}m`;
        }
        if (this.isApproveAndConvert) {
          this.isApproveAndConvert = false;
          this.convertEstimate();
        }
      }, () => {
        this.isApproveAndConvert = false;
        this.spinnerService.hideLoader(this.viewRef, this.tabCounter);
      });
  }
  setAppointment() {
    const userAppointment =  this.data['UserAppointment'];
    this.data.UserAppointments = [];
    if (userAppointment) {
      this.data.UserAppointments.push(userAppointment);
    }
    this.data.UserAppointments.forEach((x, i) => {
      if (i===0) {
        const promisedDt = this.datePipe.transform(x.AppointmentDate, "MM/dd/yyyy");
        if (x.StartTime) {
          x.StartTime = this.datePipe.transform(new Date(promisedDt + " " + x.StartTime), "h:mm a");
          if (x.EndTime) {
            const startTimeDt = new Date(promisedDt + " " + x.StartTime);
            const endTimeDt = new Date(promisedDt + " " + x.EndTime);
            const timeDiff = endTimeDt.getTime() - startTimeDt.getTime();
            const min = Math.floor((timeDiff % 3600000) / 60000);
            x.EndTime = Math.floor(timeDiff / 3600000) + "h : ";
            x.EndTime += (min == 0) ? "00 m" : min + "m";
          }
        }
        x.Status = this.commonMethods.GetHelperStatus(x.StatusID, x.SubStatusID);
        x['IsDispatchingAllowed'] = (x.Status.Code == 'assigned') ? true : false;
        x['IsUndoDispatchingAllowed'] = (x.Status.Code == 'dispatched') ? true : false;
        const status = this.commonMethods.getCodeTypeData().WorkTicketStatus.find(y => y.ID == x.StatusID);
        const sub_Status = this.commonMethods.getCodeTypeData().WorkTicketSubStatus.find(y => y.ID == x.SubStatusID);
        if (status && sub_Status) {
          this.locked = this.commonMethods.IsWorkOrderEditDisabled({ Status: status, SubStatus: sub_Status });
        }
      }
    });
  }
  setEstimateOptions() {
    let totalStandardPrice = 0;
    let totalAgreementPrice = 0;

    this.paymentDetails = new WorkTicketPaymentSummary();
    this.data.EstimateOptions.forEach(x => {
      this.paymentDetails.NetTaxable += x.NetTaxable;
      this.paymentDetails.TaxedAmount += x.TaxedAmount;
      this.paymentDetails.DiscountAmount += x.TotalDiscount;
      this.paymentDetails.TotalApproved += x.TotalApprovedPrice;
      this.paymentDetails.TotalAmount += x.Total;

      this.priceBookItems = {
        data: [],
        total: 0
      };
      x.EstimatePriceBookItems.forEach(y => {
        if (y.AgreementPrice != null && y.Price == y.OriginalPrice) {
          y.Price = y.AgreementPrice;
          totalAgreementPrice += (y.Quantity * y.AgreementPrice);     
          totalStandardPrice += (y.Quantity * y.OriginalPrice);
        }
        y.PriceBookItem.ImagePath = this.commonMethods.GetProductDefaultImg(y.PriceBookItem);
        this.priceBookItems.data.push(y);
      });
      this.priceBookItems.total = this.priceBookItems.data.length;

      this.attachments = {
        data: [],
        total: 0
      };
      x.EstimateAttachments.forEach(x => {
        const uploadedBy = x['CreatedByUser'];
        x['UploadedBy'] = !CommonMethods.CheckNullOrUndefined(uploadedBy)? `${uploadedBy.FirstName} ${uploadedBy.LastName}` : "";
        x['IsImage'] = !this.commonMethods.CheckLinkIsNotImage(x);
        this.attachments.data.push(x);
      });
      this.attachments.total = this.attachments.data.length;
      this.discountDetails = x.EstimateOptionDiscountItems[0];
    });
    this.agreementDiscount = totalStandardPrice - totalAgreementPrice;
  }
  setNotes() {
    const exTypeId = this.commonMethods.getCodeTypeData('NoteType').find(x => x.Code.toLowerCase() == "estimateexternal").ID;
    const inTypeId = this.commonMethods.getCodeTypeData('NoteType').find(x => x.Code.toLowerCase() == "estimateinternal").ID;
    this.note = {
      internal: (this.data.Notes.find(x => x.Type.ID === inTypeId))? this.data.Notes.find(x => x.Type.ID === inTypeId).Content : "",
      external: (this.data.Notes.find(x => x.Type.ID === exTypeId))? this.data.Notes.find(x => x.Type.ID === exTypeId).Content : ""
    }
  }
  viewAttachmentSlide(idx: number) {
    this.slideIndex = idx;
    this.showAttachmentSlider = true;
  }
  closeAttachmentSlider() {
    this.showAttachmentSlider = false;
  }
  openViewWorkOrder(obj) {
    obj['ID'] = obj.ID;
    obj['Customer'] = { ID: this.data.Customer.ID };
    obj['JobSite'] = { ID: this.data.JobSite.ID };
    obj['WorkTicketNo'] = obj.WorkTicketNo;
    let parmas = TabParams.ViewWorkOrder(obj);
    parmas['replaceIfOpen'] = {
      replaceTab: true,
      tabToReplace: Tabs.FsLiteWorkOrderEdit,
      action: "focus"
    }
    this.tabOpenService.OpenTab(parmas);
  }
  editEstimate() {
    const code = this.data.Status.Code.toLowerCase();
    if (['submitted', 'approved'].includes(code)) {
      this.confirmationBox.message = (code === 'submitted')? 
        "Estimate is in submitted state, are you sure you want to edit?": "Estimate is in approved state, are you sure you want to edit?";
      this.confirmationBox.show = true;
      this.confirmationBox.type = 'edit';
      return;
    } else {
      this.replaceViewEstiamteWithEditTab();
    }
  }
  replaceViewEstiamteWithEditTab() {
    this.tabOpenService.ReplaceTab({ CurrentTabIndex: this.tabCounter,
      newTabInfo: TabParams.EditEstimate({ ID: this.data.ID, EstimateNo: this.data.EstimateNo, Customer: this.data.Customer, JobSite: this.data.JobSite })
    }, false);
  }
  printEstimate() {
    this.spinnerService.showloader(this.viewRef, this.tabCounter, 0);
    this.estimateService.PrintEstimate(this.inputParams.ID)
      .pipe(takeUntil(this.ngUnsubscribe$)).subscribe(response => {
        this.printService.PrintURL(this.commonMethods.GetDocumentPrintURL(response));
        this.spinnerService.hideLoader(this.viewRef, this.tabCounter);
    }, (e) => {
         this.spinnerService.hideLoader(this.viewRef, this.tabCounter);
         if (!CommonMethods.CheckNullOrUndefined(e.error.Message))
        this.notificationService.showMultiLineError(e.error.Message, "Error");
      }
    ); 
  }
  cancelConfirmation() {
    this.confirmationBox = {
      type: '',
      message: '',
      code: '',
      show: false
    };
  }
  changeStatus(code: string) {
    if (code === 'open' && this.data.Status.Code.toLowerCase() === "approved") {
      this.confirmationBox  = {
        message : "This will clear any approval signature, are you sure you want to Undo Approval?",
        code : code,
        show : true,
        type: 'status'
      }
      return;
    }
    this.updateStatus(code);
  }
  confirm() {
    if (this.confirmationBox.type === 'status') {
      this.updateStatus(this.confirmationBox.code);
    } else if (this.confirmationBox.type === 'edit') {
      this.replaceViewEstiamteWithEditTab();
    } else if (['ExpAgreement', 'JsAgreement'].includes(this.confirmationBox.type)) {
      this.showConvertEstimate = true;
    }
    this.cancelConfirmation();
  }
  dispatchAppointment(item, action) {
    const openId = this.commonMethods.getCodeTypeData().WorkTicketStatus.find(x => x.Code.toLowerCase() == Constants.WOStaticStatus().open).ID;
    let statusModel = new CodeTypeModel();
    let subStatusModel = new CodeTypeModel();
    statusModel.ID = openId;
    statusModel.Code = Constants.WOStaticStatus().open; 
    switch(action) {
    case 0: 
      subStatusModel.ID = this.commonMethods.getCodeTypeData().WorkTicketSubStatus.find(x => x.Code.toLowerCase() == Constants.WOStaticStatus().dispatched).ID;
      subStatusModel.Code = Constants.WOStaticStatus().dispatched;
      break;
    default:
      subStatusModel.ID = this.commonMethods.getCodeTypeData().WorkTicketSubStatus.find(x => x.Code.toLowerCase() == Constants.WOStaticStatus().assigned).ID;
      subStatusModel.Code = Constants.WOStaticStatus().assigned;
      break;
    }
    this.spinnerService.showloader(this.viewRef, this.tabCounter, 0);
    this.dispatcherService.UpdateDispatchEstimate_Patch(item.ID, statusModel, subStatusModel)
      .pipe(takeUntil(this.ngUnsubscribe$)).subscribe((res) => {
        item.StatusID = statusModel.ID;
				item.SubStatusID = subStatusModel.ID;
				item.Status = this.commonMethods.GetHelperStatus(statusModel.ID, subStatusModel.ID);
        if (!['dispatched', 'rescheduled'].includes(subStatusModel.Code.toLowerCase())) {
          item.IsDispatchingAllowed = true;
          item.IsUndoDispatchingAllowed = false;
        } else if (subStatusModel.Code.toLowerCase() === "dispatched") {
          item.IsUndoDispatchingAllowed = true;
          item.IsDispatchingAllowed = false;
        }
        this.notificationService.success(action === 0 ? Constants.DefaultWorkOrderMessages.HelperDispatchedMessage : Constants.DefaultWorkOrderMessages.HelperUndoDispatchedMessage, "Success");		  
        this.spinnerService.hideLoader(this.viewRef, this.tabCounter);
      }, (error) => {
        if (!CommonMethods.CheckNullOrUndefined(error.error.Message))
				this.notificationService.showMultiLineError(error.error.Message, "Error");
        this.spinnerService.hideLoader(this.viewRef, this.tabCounter);
      });
  }
  convertEstimate() {
    let callJsAgreement = true;
    this.data.EstimateOptions.forEach(x => {
      x.EstimatePriceBookItems.forEach(y => {
        if(y.ServiceAgreement) {
          if (['Expired', 'Suspended', 'Canceled'].includes(y.ServiceAgreement.AgreementStatus)) {
            this.confirmationBox = {
              message: 'This estimate includes service agreement pricing that has since expired.',
              code: '',
              show: true,
              type: 'ExpAgreement'
            }
            return;
          } 
          if (this.confirmationBox.show ==false) {
            this.showConvertEstimate = true;
            return;
          }
        }
      });
    });

    if (callJsAgreement) {
      this.spinnerService.showloader(this.viewRef, this.tabCounter, 0);
      this.workOrderService
        .GetPricingForItems({}, "", this.data.JobSite.ID, [], [])
          .subscribe((res : any) => {
            this.spinnerService.hideLoader(this.viewRef, this.tabCounter);
            const prices = res.Data.filter(x => x.AgreementPrice!=null);  
            if(prices.length > 0) {
                this.confirmationBox = {
                  message: 'The job site currently has 1 or more active service agreements that are not included in estimate price(s).',
                  code: '',
                  show: true,
                  type: 'JsAgreement'
                }
                return;
              }
              if (this.confirmationBox.show ==false) {this.showConvertEstimate = true;}
            }, (error) => {
            this.spinnerService.hideLoader(this.viewRef, this.tabCounter);
          });
    }
  }
  hideConvertEstimateModal() {
    this.showConvertEstimate = false;
  }
  approveAndConvertEstimate() {
    this.changeStatus('approved');
    this.isApproveAndConvert = true;
  }
  private getStatusSuccessMessage(newStatus: string) {
    let msg = "";
    const currentStatus = this.data.Status.Code.toLowerCase();
    switch(newStatus) {
      case 'submitted':
        msg = "Success! Estimate has been successfully updated to Submitted.";
        break;
      case 'approved':
        msg = "Success! Estimate has been Approved.";
        break;
      case 'declined':
        msg = "Success! Estimate has been Declined.";
        break;
      case 'open':
        if (currentStatus === 'approved') {
          msg = "Success! Estimate approval has been cleared.";
        } else if (['submitted', 'declined'].includes(currentStatus)) {
          msg = "Success! Estimate updated to Open.";
        }
        break;
      default:
        break;
    }

    return msg;
  }
  private updateStatus(code: string) {
    const status = this.commonMethods.getCodeTypeData().EstimateStatus.find(x => x.Code.toLowerCase() == code);
    this.spinnerService.showloader(this.viewRef, this.tabCounter, 0);
    this.estimateService.UpdateEstimateStatus(this.inputParams.CustomerID, this.inputParams.JobSiteID, this.inputParams.ID, status)
      .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe(() => {
          this.spinnerService.hideLoader(this.viewRef, this.tabCounter);
          const msg = this.getStatusSuccessMessage(code);
          this.notificationService.success(msg);
          this.getData();
        }, () => {
          this.isApproveAndConvert = false;
          this.spinnerService.hideLoader(this.viewRef, this.tabCounter);
        });
  }
}
